/**
 * Sugestões / Reclamações (Ouvidoria) — Portal do Condômino (Fase 2, 0.1.32).
 * O morador abre chamados e acompanha os seus; o síndico vê todos, responde e muda status.
 * 0.1.88: fotos de evidência (ex.: cachorro sem coleira no corredor), na abertura e depois.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, MessageSquarePlus, Plus, Send } from "lucide-react";
import { api, ApiError, type FotoAnexo } from "../lib/api";
import { EscolherFotos, FotosEvidencia, enviarFotos } from "./FotosEvidencia";

interface Chamado { id: number; tipo: string; assunto: string; descricao: string | null; status: string; protocolo: string; unidade: string | null; usuario_nome: string | null; criado_em: string | null; meu: boolean }
interface ChamadosResp { condominio_id: number; itens: Chamado[]; pode_gerir: boolean }
interface Interacao { autor_nome: string | null; eh_gestao: boolean; texto: string; em: string | null }
interface Detalhe extends Chamado { interacoes: Interacao[]; pode_gerir: boolean; fotos: FotoAnexo[] }

const TIPO_LABEL: Record<string, string> = { sugestao: "Sugestão", reclamacao: "Reclamação", ocorrencia: "Ocorrência" };
const STATUS_LABEL: Record<string, string> = { aberto: "Aberto", em_analise: "Em análise", resolvido: "Resolvido" };
const CORES: Record<string, string> = { aberto: "bg-amber-100 text-amber-800", em_analise: "bg-sky-100 text-sky-800", resolvido: "bg-emerald-100 text-emerald-800" };
const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

export function ChamadosPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const invalLista = () => qc.invalidateQueries({ queryKey: ["chamados", condominioId] });
  const { data } = useQuery<ChamadosResp>({ queryKey: ["chamados", condominioId], queryFn: () => api.get(`/condominios/${condominioId}/chamados`) });

  const [aberto, setAberto] = useState<number | null>(null);   // chamado selecionado (detalhe)
  const [novo, setNovo] = useState(false);
  const [tipo, setTipo] = useState("reclamacao"); const [assunto, setAssunto] = useState(""); const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [fotos, setFotos] = useState<File[]>([]);

  const criar = useMutation({
    mutationFn: async () => {
      const c = await api.post<Chamado>(`/condominios/${condominioId}/chamados`, { tipo, assunto, descricao });
      if (fotos.length) {
        try { await enviarFotos("chamados", c.id, fotos); }
        catch (e) {
          // O chamado já foi aberto; avisa que a foto não foi (dá para anexar no detalhe).
          throw new Error("Chamado aberto, mas a foto não foi enviada: "
            + (e instanceof ApiError ? e.message : "tente anexar de novo abrindo o chamado."));
        }
      }
      return c;
    },
    onSuccess: () => { setNovo(false); setAssunto(""); setDescricao(""); setTipo("reclamacao"); setFotos([]); setErro(null); invalLista(); },
    onError: (e) => { setErro(e instanceof Error ? e.message : "Não foi possível abrir o chamado."); invalLista(); },
  });

  if (aberto != null) return <DetalheChamado id={aberto} onVoltar={() => { setAberto(null); invalLista(); }} />;

  const itens = data?.itens ?? [];
  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Voltar</button>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><MessageSquarePlus size={20} /></div>
          <h1 className="text-2xl font-bold text-slate-900">Sugestões / Reclamações</h1>
        </div>
        {!novo && <button onClick={() => { setNovo(true); setErro(null); }} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800"><Plus size={16} /> Abrir</button>}
      </div>
      {erro && <div className="mb-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}

      {novo && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 space-y-2">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white">
            <option value="reclamacao">Reclamação</option><option value="sugestao">Sugestão</option><option value="ocorrencia">Ocorrência</option>
          </select>
          <input placeholder="Assunto" value={assunto} onChange={(e) => setAssunto(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <textarea placeholder="Descreva…" rows={4} value={descricao} onChange={(e) => setDescricao(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <EscolherFotos arquivos={fotos} onChange={setFotos} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setNovo(false)} className="px-3 py-2 text-sm text-slate-600">Cancelar</button>
            <button onClick={() => criar.mutate()} disabled={!assunto || !descricao || criar.isPending} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Enviar</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {itens.length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center">Nenhum chamado ainda.</div>}
        {itens.map((c) => (
          <button key={c.id} onClick={() => setAberto(c.id)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-300">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-slate-900">{c.assunto}</div>
              <span className={`px-2 py-0.5 rounded-md text-[11px] ${CORES[c.status] ?? "bg-slate-100"}`}>{STATUS_LABEL[c.status] ?? c.status}</span>
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">{TIPO_LABEL[c.tipo] ?? c.tipo} · protocolo {c.protocolo}{data?.pode_gerir && c.usuario_nome ? ` · ${c.usuario_nome}` : ""} · {fmt(c.criado_em)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetalheChamado({ id, onVoltar }: { id: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const { data: c } = useQuery<Detalhe>({ queryKey: ["chamado", id], queryFn: () => api.get(`/chamados/${id}`) });
  const [texto, setTexto] = useState(""); const [status, setStatus] = useState<string>("");
  const responder = useMutation({
    mutationFn: () => api.post(`/chamados/${id}/responder`, { texto, status: status || null }),
    onSuccess: () => { setTexto(""); setStatus(""); qc.invalidateQueries({ queryKey: ["chamado", id] }); },
  });

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Voltar</button>
      {!c ? <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center"><Loader2 size={16} className="animate-spin" /> Carregando…</div> : (
        <>
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-xl font-bold text-slate-900">{c.assunto}</h1>
            <span className={`px-2 py-0.5 rounded-md text-[11px] ${CORES[c.status] ?? "bg-slate-100"}`}>{STATUS_LABEL[c.status] ?? c.status}</span>
          </div>
          <div className="text-[12px] text-slate-500 mb-3">{TIPO_LABEL[c.tipo] ?? c.tipo} · protocolo {c.protocolo}{c.usuario_nome ? ` · ${c.usuario_nome}` : ""}</div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap mb-3">
            {c.descricao}
            <FotosEvidencia caminho="chamados" registroId={c.id} fotos={c.fotos ?? []}
              podeEnviar={c.meu || c.pode_gerir} podeApagar={c.meu || c.pode_gerir}
              onMudou={() => qc.invalidateQueries({ queryKey: ["chamado", id] })} />
          </div>

          <div className="space-y-2 mb-4">
            {c.interacoes.map((i, k) => (
              <div key={k} className={`rounded-xl p-3 text-sm ${i.eh_gestao ? "bg-brand-50 border border-brand-100" : "bg-slate-50 border border-slate-200"}`}>
                <div className="text-[11px] text-slate-500 mb-0.5">{i.autor_nome}{i.eh_gestao ? " (síndico)" : ""} · {fmt(i.em)}</div>
                <div className="whitespace-pre-wrap text-slate-700">{i.texto}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
            <textarea placeholder="Escreva uma resposta…" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="flex items-center justify-between gap-2">
              {c.pode_gerir ? (
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white">
                  <option value="">Manter status</option><option value="aberto">Aberto</option><option value="em_analise">Em análise</option><option value="resolvido">Resolvido</option>
                </select>
              ) : <span />}
              <button onClick={() => responder.mutate()} disabled={!texto || responder.isPending} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                {responder.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
